import {ReactNode, SVGProps, useEffect, useRef} from 'react';
import {useSettings} from '@common/core/settings/use-settings';
import {Link, useLocation} from 'react-router-dom';

export const c = {
  bg: '#ffffff',
  ink: '#05173B',
  ink2: '#06122F',
  inkMuted: '#60708A',
  inkSoft: '#8B98AD',
  rule: 'rgba(5, 23, 59, 0.12)',
  ruleSoft: '#F4FFFD',
  lime: '#A3D603',
  limeDeep: '#00C8FF',
  limeSoft: 'rgba(163, 214, 3, 0.14)',
  navy: '#05173B',
  cyan: '#A3D603',
  cyanDark: '#00C8FF',
  green: '#8EB800',
};

export function Container({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mx-auto w-full max-w-1280 px-24 md:px-40 ${className}`}>
      {children}
    </div>
  );
}

export function SiteHeader() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      if (window.scrollY > 8) el.setAttribute('data-scrolled', 'true');
      else el.removeAttribute('data-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {passive: true});
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header ref={ref} className="mrigl-public-header sticky top-0 z-50">
      <div className="mrigl-top-banner">
        Nouveau : <span>Votre site en ligne à partir de 8 TND/mois</span> -
        SSL, hébergement et modèles inclus.
      </div>
      <Container className="mrigl-nav-inner flex items-center justify-between gap-16 py-16">
        <Wordmark />
        <nav
          className="mrigl-nav-links hidden items-center gap-10 md:flex"
          aria-label="Navigation"
        >
          <HeaderHashLink hash="modeles">Modèles</HeaderHashLink>
          <HeaderTarifLink />
          <HeaderHashLink hash="faq">Questions</HeaderHashLink>
          <Link to="/contact" className="header-link text-sm">
            Contact
          </Link>
        </nav>
        <div className="mrigl-nav-actions flex items-center gap-10">
          <Link
            to="/login"
            className="nav-cta nav-login inline-flex items-center justify-center rounded px-16 py-8 text-sm font-bold"
          >
            Connexion
          </Link>
          <Link
            to="/register"
            className="nav-cta-primary hidden items-center justify-center rounded px-18 py-10 text-sm font-extrabold sm:inline-flex"
          >
            Créer mon site
          </Link>
        </div>
      </Container>
    </header>
  );
}

function HeaderHashLink({
  hash,
  children,
}: {
  hash: string;
  children: ReactNode;
}) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const href = `#${hash}`;

  if (isHome) {
    return (
      <a href={href} className="header-link text-sm">
        {children}
      </a>
    );
  }

  return (
    <Link to={`/${href}`} className="header-link text-sm">
      {children}
    </Link>
  );
}

function HeaderTarifLink() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  if (isHome) {
    // Same-page hash navigation uses native browser scroll.
    return (
      <a href="#creer" className="header-link text-sm">
        Tarif
      </a>
    );
  }
  return (
    <Link to="/#creer" className="header-link text-sm">
      Tarif
    </Link>
  );
}

export function Wordmark() {
  const {branding} = useSettings();
  const logo = branding?.logo_dark || branding?.logo_light;

  if (logo) {
    return (
      <Link
        to="/"
        aria-label="MRIGL.site, accueil"
        className="mrigl-wordmark inline-flex"
      >
        <img
          src={logo}
          alt={branding?.site_name || 'MRIGL.site'}
          className="h-52 max-h-52 w-auto"
        />
      </Link>
    );
  }

  return (
    <Link
      to="/"
      aria-label="MRIGL.site, accueil"
      className="mrigl-wordmark inline-flex items-baseline text-2xl"
      style={{color: c.ink, fontWeight: 900, letterSpacing: 0}}
    >
      mrigl
      <span style={{color: c.cyan}}>.</span>
      <span style={{color: c.inkMuted, fontWeight: 800}}>site</span>
    </Link>
  );
}

export function SiteFooter() {
  return (
    <footer className="mrigl-public-footer border-t py-42">
      <Container>
        <div className="footer-inner flex flex-wrap items-center justify-between gap-x-32 gap-y-16">
          <div className="flex flex-wrap items-center gap-x-20 gap-y-8">
            <Wordmark />
            <span className="text-sm" style={{color: c.inkMuted}}>
              Des sites simples, à un prix honnête.
            </span>
          </div>
          <nav
            className="flex flex-wrap gap-x-24 gap-y-8"
            aria-label="Liens secondaires"
          >
            <FooterLink href="https://mrigl.site/pages/privacy-policy">
              Politique de confidentialité
            </FooterLink>
            <FooterLink href="https://mrigl.site/pages/terms-of-service">
              Conditions d'utilisation
            </FooterLink>
            <FooterLink href="https://mrigl.site/contact">
              Nous contacter
            </FooterLink>
          </nav>
        </div>
      </Container>
    </footer>
  );
}

function FooterLink({href, children}: {href: string; children: ReactNode}) {
  return (
    <a
      href={href}
      className="text-sm transition-colors"
      style={{color: c.inkMuted, textDecoration: 'none'}}
      onMouseEnter={e => (e.currentTarget.style.color = c.ink)}
      onMouseLeave={e => (e.currentTarget.style.color = c.inkMuted)}
    >
      {children}
    </a>
  );
}

export function Reveal({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      el.classList.add('in');
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      {rootMargin: '0px 0px -8% 0px', threshold: 0.08},
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
}

export function ArrowRight(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M2 7h10M8 3l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CheckMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M2.5 7.5l3 3 6-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
